import React, { useState } from 'react';
import { Popover, IconButton, Tooltip, makeStyles } from '@material-ui/core';
import ContentCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import { COLORS } from './logsConstants';
import JsonTreeNode from '../../atoms/JsonTreeViewer';
import { parseMessageSegments } from './messageParser';

const useStyles = makeStyles(() => ({
  codeChip: {
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    fontSize: 12,
    backgroundColor: '#F1F5F9',
    color: '#334155',
    fontWeight: 600,
    padding: '1px 5px',
    borderRadius: 4,
  },
  jsonChip: {
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    fontSize: 12,
    backgroundColor: '#F5F3FF',
    color: '#7C3AED', // violet — distinct from the link menu's blue
    fontWeight: 600,
    padding: '1px 5px',
    borderRadius: 4,
    cursor: 'pointer',
    border: '1px solid transparent',
    '&:hover': {
      borderColor: '#7C3AED',
    },
  },
  popoverPaper: {
    borderRadius: 10,
    boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
    border: `1px solid ${COLORS.border}`,
  },
  popoverHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: `1px solid ${COLORS.border}`,
    backgroundColor: '#fafbfc',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  popoverTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
  copyBtn: {
    padding: 5,
    color: COLORS.textMuted,
    '&:hover': { color: COLORS.textPrimary },
  },
  popoverBody: {
    maxWidth: 520,
    maxHeight: 400,
    overflow: 'auto',
    padding: '12px 14px',
  },
  highlight: {
    backgroundColor: '#FEF08A',
    color: 'inherit',
    borderRadius: 2,
    padding: '0 1px',
    fontWeight: 600,
  },
}));

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightMatch(text, query, classes) {
  if (!query || !text) return text;
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const parts = String(text).split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i} className={classes.highlight}>{part}</mark> : part
  );
}

function JsonChip({ value, parsed, classes }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const isArray = Array.isArray(parsed);
  const typeLabel = isArray ? `Array · ${parsed.length} items` : `Object · ${Object.keys(parsed).length} keys`;

  function handleCopy(e) {
    e.stopPropagation();
    const text = JSON.stringify(parsed, null, 2);
    // Capture this synchronously, NOT inside fallbackCopy — React pools/nullifies synthetic
    // events right after this handler returns, so e.currentTarget would already be null by
    // the time an async .catch() callback tries to read it.
    const anchorButton = e.currentTarget;

    function showSuccess() {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }

    function fallbackCopy() {
      const container = (anchorButton && anchorButton.closest('.MuiPopover-paper')) || document.body;

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      textarea.style.top = '0';
      textarea.style.left = '0';
      container.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        const ok = document.execCommand('copy') && textarea.value === text;
        if (ok) showSuccess();
        else console.error('Copy to clipboard did not succeed');
      } catch (err) {
        console.error('Copy to clipboard failed', err);
      } finally {
        container.removeChild(textarea);
      }
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(showSuccess).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }

  return (
    <>
      <span
        className={classes.jsonChip}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        {isArray ? `[ ${parsed.length} ${parsed.length === 1 ? 'item' : 'items'} ]` : `{ ${Object.keys(parsed).length} ${Object.keys(parsed).length === 1 ? 'key' : 'keys'} }`}
      </span>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={(e) => { e && e.stopPropagation && e.stopPropagation(); setAnchorEl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        classes={{ paper: classes.popoverPaper }}
        elevation={0}
      >
        <div className={classes.popoverHeader}>
          <span className={classes.popoverTitle}>{typeLabel}</span>
          <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
            <IconButton size="small" className={classes.copyBtn} onClick={handleCopy}>
              {copied ? <CheckIcon style={{ fontSize: 16 }} /> : <ContentCopyIcon style={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.popoverBody}>
          <JsonTreeNode value={parsed} depth={0} />
        </div>
      </Popover>
    </>
  );
}

export default function MessageContent({ text, query }) {
  const classes = useStyles();
  const segments = parseMessageSegments(text || '');

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'json') {
          return <JsonChip key={i} value={seg.value} parsed={seg.parsed} classes={classes} />;
        }
        if (seg.type === 'path') {
          return <span key={i} className={classes.codeChip}>{seg.value}</span>;
        }
        return <React.Fragment key={i}>{highlightMatch(seg.value, query, classes)}</React.Fragment>;
      })}
    </>
  );
}